import React, { useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import ApprovalListItem from './ApprovalListItem';
import '../styles/ApprovalList.scss';
import NoApprovals from './NoApprovals';
import { type ReturnUseApprovalsProps, type ApprovalCustomComponents, type ApprovalObject, type IApprovalTab } from 'modules/cbre-approvals-widget/types';
import ApprovalActions from './ApprovalActions';
import useApprovalListener from 'modules/cbre-approvals-widget/components/useApprovalListener';


interface ApprovalListProps {
    selectedApproval: ApprovalObject | null;
    onApprovalClick: (approval: ApprovalObject) => void;
    onLoadMore: (count:number) => void;
    components: ApprovalCustomComponents
    config: ReturnUseApprovalsProps;
    tabValue: IApprovalTab["tab"]
}

const ApprovalList: React.FC<ApprovalListProps> = ({
    onApprovalClick,
    onLoadMore,
    selectedApproval,
    components,
    config,
    tabValue
}) => {
    const [height, setHeight] = useState(300);
    const listContainer = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<any>(null);
    const { pageSize, approvals, loading  } = config.approvalsList;    

    // Scroll to selected approval
    useEffect(() => {
        if (selectedApproval && listRef.current && approvals.length > 0) {
            const selectedIndex = approvals.findIndex((approval: ApprovalObject) => {
                if (tabValue.value === "externalApprovals" || tabValue.value === "externalApprovalsHistory") {
                    return selectedApproval?.approvalFlow?.approvalFlowId === approval?.approvalFlow?.approvalFlowId;
                }
                return selectedApproval?.approvalTaskId === approval.approvalTaskId;
            });
            
            if (selectedIndex !== -1) {
                listRef.current.scrollToItem(selectedIndex, 'smart');
            }
        }
    }, [selectedApproval, approvals, tabValue]);    

    useEffect(() => {
        const handleResize = () => {
            if (listContainer.current) {
                const height = window.innerHeight - listContainer.current.offsetTop - 20;
                if (height > 300) setHeight(height);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Call initially to set the correct state

        return () => {
            window.removeEventListener('resize', handleResize);
        };
        
    }, [listContainer.current, approvals.length]);

    

    const loadingSkeleton = () => {
        return (
            <Box className="p-2" style ={{height: 80, borderBottom: "1px solid #003F2D26"}} >
                <div className='d-flex justify-content-between w-100'>
                <Skeleton animation="wave"  width={"40%"} height={20} />
                <Skeleton animation="wave"  width={"30%"} height={20} />
                </div>                
                <Skeleton  width={"60%"} height={20} />
                <Skeleton  width={"80%"} height={20} />
            </Box>
        );
    }

    return (
        <Box className="approval-list-section">
            { <Box className="approval-list-header">
                <ApprovalActions config={config} components={components} />
               {/* {components.Action && <components.Action config={config}   />} */}
            </Box>}
            <div className="virtual-list-container" ref={listContainer}>
                {loading ? <div>
                    {Array.from({length: Math.round(Math.ceil(height / 100))}).map(()=> loadingSkeleton() )}
                </div> : approvals.length === 0 ? (
                    <NoApprovals height={height} />
                ) : (
                    <List
                        ref={listRef}
                        height={height}
                        itemCount={approvals.length}
                        itemSize={80}
                        width="100%"
                        onItemsRendered={({ visibleStopIndex }: any) => {
                            
                            if (visibleStopIndex === approvals.length - 1) {
                                console.log(visibleStopIndex, "visibleStopIndex")
                                onLoadMore(visibleStopIndex);
                            }
                        }}
                    >
                        {({ index, style }: any) => (
                            <ApprovalListItem
                                approval={approvals[index]}
                                onClick={() => onApprovalClick(approvals[index])}
                                style={style}
                                selectedApproval={selectedApproval}
                                tabValue={tabValue}
                            />
                        )}
                    </List>
                )}
            </div>
        </Box>
    );
};

export default ApprovalList;
